import { inject, Injectable } from '@angular/core';
import { SOCKET_EVENTS } from '@constants/app.constants';
import { EmailVerified } from '@models/auth.model';
import { ActivitySocketEvent } from '@models/process.model';
import { Socket } from 'ngx-socket-io';
import { Observable } from 'rxjs';
import { NotificationsService } from './notifications.service';
import { UserStateService } from './user-state.service';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private readonly socket = inject(Socket);
  private readonly notificationsService = inject(NotificationsService);
  private readonly userStateService = inject(UserStateService);
  private isConnected = false;

  constructor() {
    this.setupEventListeners();
  }

  connect(): void {
    if (!this.socket.ioSocket.connected) {
      // Function form (not a static object) so socket.io-client re-reads the
      // current token from UserStateService on every connect AND automatic
      // reconnect — a stale token baked in once would break silently after a
      // token refresh. Backend independently verifies this (NI-14); it is
      // no longer enough to just claim a user_id.
      this.socket.ioSocket.auth = (
        cb: (data: { token: string | null }) => void
      ) => cb({ token: this.userStateService.getToken() });
      this.socket.connect();
    }
  }

  disconnect(): void {
    if (this.isConnected) {
      this.socket.disconnect();
      this.isConnected = false;
    }
  }

  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  private joinUserRoom(): void {
    const userData = this.userStateService.getUserData();
    const userId = userData()?.uuid;

    if (!userId) {
      return;
    }

    this.socket.emit(SOCKET_EVENTS.JOIN_USER_ROOM, { userId });
  }

  listenForActivityUpdates(): Observable<ActivitySocketEvent> {
    return this.socket.fromEvent<ActivitySocketEvent>(
      SOCKET_EVENTS.ACTIVITY_STATUS_UPDATE
    );
  }

  private setupEventListeners(): void {
    // Connection events
    this.socket.on(SOCKET_EVENTS.CONNECT, () => {
      this.isConnected = true;
      this.joinUserRoom();
    });

    this.socket.on('connected', (data: { message: string }) => {
      console.log('Connected message from server:', data.message);
    });

    this.socket.on(SOCKET_EVENTS.CRED_NOTIFICATION, () => {
      this.notificationsService.handleNewNotificationFromSocket();
    });

    this.socket.on(SOCKET_EVENTS.TASK_COMPLETION, () => {
      this.notificationsService.handleNewNotificationFromSocket();
    });

    this.socket.on(SOCKET_EVENTS.DISCONNECT, () => {
      this.isConnected = false;
    });

    this.socket.on(SOCKET_EVENTS.CONNECT_ERROR, (error: Error) => {
      console.error('Socket connection error:', error);
      this.isConnected = false;
    });

    this.socket.on(SOCKET_EVENTS.RECONNECT, () => {
      this.isConnected = true;
    });

    this.socket.on(SOCKET_EVENTS.RECONNECT_FAILED, () => {
      console.error('Socket reconnection failed');
    });
  }

  listenForVerificationStatus() {
    return this.socket.fromEvent<EmailVerified>('user_email_confirmation');
  }

  /**
   * Listens for onboarding process completed (e.g. after background credential issuance).
   * Emits when the backend has finished the onboarding flow.
   */
  listenForOnboardingProcessCompleted(): Observable<unknown> {
    return this.socket.fromEvent(SOCKET_EVENTS.ONBOARDING_PROCESS_COMPLETED);
  }

  /**
   * Listens for new credential request events.
   */
  listenForNewRequest(): Observable<unknown> {
    return this.socket.fromEvent(SOCKET_EVENTS.NEW_REQUEST);
  }
}
